// apps/client/src/components/offices/TransferBrokerModal.tsx
// Enterprise-grade broker transfer confirmation modal
// Google Pattern: "Make the error case impossible"

import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';

interface TransferBrokerModalProps {
  show: boolean;
  onClose: () => void;
  broker: {
    id: string;
    name: string;
  } | null;
  sourceOffice: {
    id: string;
    name: string;
  } | null;
  targetOffice: {
    id: string;
    name: string;
  } | null;
  onTransferComplete: (_success: boolean, _message: string) => void;
  availableOffices?: Array<{ id: string; name: string }>; // For manual selection
}

type TransferReason = 'promotion' | 'reorganization' | 'relocation' | 'performance' | 'other';

const TransferBrokerModal: React.FC<TransferBrokerModalProps> = ({
  show,
  onClose,
  broker,
  sourceOffice,
  targetOffice: initialTargetOffice,
  onTransferComplete,
  availableOffices = []
}) => {
  const [reason, setReason] = useState<TransferReason | ''>('');
  const [notes, setNotes] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [targetOffice, setTargetOffice] = useState(initialTargetOffice);
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState('');

  // Update target office when prop changes (from drag-and-drop)
  useEffect(() => {
    setTargetOffice(initialTargetOffice);
  }, [initialTargetOffice]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setReason('');
      setNotes('');
      setEffectiveDate(new Date().toISOString().split('T')[0]);
      setError('');
      setTargetOffice(initialTargetOffice);
    }
  }, [show, initialTargetOffice]);

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!broker || !targetOffice || !reason) {
      setError('Please fill in all required fields');
      return;
    }

    if (sourceOffice && sourceOffice.id === targetOffice.id) {
      setError('Cannot transfer to the same office');
      return;
    }

    setTransferring(true);

    try {
      const transferBrokerFn = httpsCallable(functions, 'transferBroker');

      const result = await transferBrokerFn({
        brokerId: broker.id,
        targetOfficeId: targetOffice.id,
        reason,
        notes: notes.trim(),
        effectiveDate: effectiveDate || undefined
      });

      const response = result.data as { success: boolean; message: string };

      if (response.success) {
        onTransferComplete(true, response.message);
        onClose();
      } else {
        setError(response.message || 'Transfer failed');
        onTransferComplete(false, response.message);
      }
    } catch (err: any) {
      console.error('Transfer error:', err);
      const errorMessage = err.message || 'Failed to transfer broker. Please try again.';
      setError(errorMessage);
      onTransferComplete(false, errorMessage);
    } finally {
      setTransferring(false);
    }
  };

  const reasonOptions: Array<{ value: TransferReason; label: string; description: string }> = [
    {
      value: 'promotion',
      label: 'Promotion',
      description: 'Broker promoted to new role/office'
    },
    {
      value: 'reorganization',
      label: 'Reorganization',
      description: 'Company restructuring or office realignment'
    },
    {
      value: 'relocation',
      label: 'Relocation',
      description: 'Broker relocated to new geographic area'
    },
    {
      value: 'performance',
      label: 'Performance',
      description: 'Performance-related transfer'
    },
    {
      value: 'other',
      label: 'Other',
      description: 'Other reason (please specify in notes)'
    }
  ];

  if (!broker) return null;

  return (
    <Modal
      title="Transfer Broker"
      show={show}
      onClose={onClose}
    >
      <form onSubmit={handleTransfer}>
        {/* Transfer Summary */}
        <div className="bg-gray-700 rounded-lg p-4 mb-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Broker:</span>
              <span className="text-white font-medium">{broker.name}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">From:</span>
              <span className="text-white">{sourceOffice?.name || 'Unassigned'}</span>
            </div>

            <div className="flex items-center justify-center text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">To:</span>
              {/* Allow manual selection if no target office pre-selected */}
              {!initialTargetOffice && availableOffices.length > 0 ? (
                <select
                  value={targetOffice?.id || ''}
                  onChange={(e) => {
                    const selected = availableOffices.find(o => o.id === e.target.value);
                    setTargetOffice(selected || null);
                  }}
                  className="bg-gray-600 border border-gray-500 text-white rounded-md px-3 py-1 text-sm"
                  required
                >
                  <option value="">Select office...</option>
                  {availableOffices
                    .filter(o => o.id !== sourceOffice?.id)
                    .map(office => (
                      <option key={office.id} value={office.id}>
                        {office.name}
                      </option>
                    ))}
                </select>
              ) : (
                <span className="text-white font-medium">{targetOffice?.name || 'Select office'}</span>
              )}
            </div>
          </div>
        </div>

        {/* Reason (Required) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Reason for Transfer <span className="text-red-400">*</span>
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value as TransferReason)}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2"
            required
          >
            <option value="">Select a reason...</option>
            {reasonOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {reason && (
            <p className="mt-1 text-xs text-gray-400">
              {reasonOptions.find(o => o.value === reason)?.description}
            </p>
          )}
        </div>

        {/* Effective Date */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Effective Date
          </label>
          <input
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2"
            max={new Date().toISOString().split('T')[0]} // Can't be future date
          />
          <p className="mt-1 text-xs text-gray-400">
            When did this transfer take effect? (defaults to today)
          </p>
        </div>

        {/* Notes (Optional) */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Notes {reason === 'other' && <span className="text-red-400">*</span>}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2"
            rows={3}
            placeholder="Optional additional context for this transfer..."
            required={reason === 'other'}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4 bg-red-900 border border-red-700 text-red-200 p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Important Notice */}
        <div className="mb-6 bg-blue-900 border border-blue-700 text-blue-200 p-3 rounded-md text-sm">
          <p className="font-medium mb-1">📌 Important:</p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>Trail commission will remain attributed to the <strong>originating office</strong></li>
            <li>New loans from this broker will be attributed to <strong>{targetOffice?.name || 'the new office'}</strong></li>
            <li>This transfer will be recorded in the broker's history and audit log</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={transferring}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={transferring || !reason || !targetOffice}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500 disabled:opacity-50 flex items-center gap-2"
          >
            {transferring ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Transferring...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Confirm Transfer
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TransferBrokerModal;
